import React from 'react';
import { ChevronDown, ChevronRight, Table2, Trash2 } from 'lucide-react';
import { SOURCE_META } from '../dataModelsConstants';
import type { DataModel, DataSource, ModelTable } from '../dataModelsTypes';
import { formatRowCount, sourceById } from '../dataModelsUtils';
import { ModelIconTile, StatusPill, ViewRowButton, iconBtn, surfaces } from './dataModelsUi';

export default function DataModelCard({
  isLight,
  model,
  tables,
  sources,
  expanded,
  onToggle,
  onSelectTable,
  onOpen,
  onDelete,
}: {
  isLight: boolean;
  model: DataModel;
  tables: ModelTable[];
  sources: DataSource[];
  expanded: boolean;
  onToggle: () => void;
  onSelectTable: (tableId: string) => void;
  onOpen?: () => void;
  onDelete?: () => void;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const border = ui.border;

  return (
    <article className={`overflow-hidden rounded-md border ${ui.card}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <ModelIconTile isLight={isLight} />
          <span className="min-w-0">
            <span className={`block truncate text-[15px] font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{model.name}</span>
            <span className={`mt-0.5 block text-xs ${muted}`}>{tables.length} assets</span>
          </span>
        </div>
        <StatusPill status={model.status} isLight={isLight} />
        {onDelete ? (
          <button
            type="button"
            className={iconBtn(isLight)}
            title="Delete data model"
            aria-label={`Delete ${model.name}`}
            onClick={onDelete}
          >
            <Trash2 size={14} />
          </button>
        ) : null}
        {onOpen ? <ViewRowButton isLight={isLight} label={`View ${model.name}`} onClick={onOpen} /> : null}
        <button
          type="button"
          onClick={onToggle}
          aria-label={expanded ? 'Hide assets' : 'Show assets'}
          className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${isLight ? 'text-[#5C6570] hover:bg-[#F2F4F7]' : 'text-slate-300 hover:bg-white/10'}`}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>

      {expanded ? (
        <div className={`border-t ${border}`}>
          <div className={`border-b px-4 ${border}`}>
            <span className={`relative inline-block py-2.5 text-sm font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
              Assets
              <span className={`absolute inset-x-0 -bottom-px h-0.5 ${isLight ? 'bg-[#1C1C1C]' : 'bg-white'}`} />
            </span>
          </div>
          {tables.length ? (
            <table className="min-w-full text-[13px]">
              <thead className={ui.tableHead}>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Rows</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="w-10 px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {tables.map((table) => {
                  const source = sourceById(sources, table.sourceId);
                  const SourceIcon = source ? SOURCE_META[source.kind].icon : null;
                  return (
                    <tr key={table.id} className={`border-t ${border} ${ui.row}`}>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <Table2 size={14} className={muted} />
                          <span className={`font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{table.name}</span>
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 ${muted}`}>
                        {source && SourceIcon ? (
                          <span className="inline-flex items-center gap-1.5">
                            <SourceIcon size={13} strokeWidth={1.75} />
                            {source.name}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">{formatRowCount(table.rows)}</td>
                      <td className="px-3 py-2.5">
                        <StatusPill status={table.status} isLight={isLight} />
                      </td>
                      <td className="px-3 py-2.5">
                        <ViewRowButton isLight={isLight} label={`View ${table.name}`} onClick={() => onSelectTable(table.id)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className={`px-4 py-6 text-sm ${muted}`}>No assets in this model yet.</p>
          )}
        </div>
      ) : null}
    </article>
  );
}
